import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Provider } from '../models/provider.model';
import { BehaviorSubject, first, Observable, of, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { BaseService } from '../services/_base.service';
import { UserService } from '../services/user.service';
import { Send } from 'express';

@Injectable(
  {
    providedIn: 'root',
  },
)
export class ProviderService extends BaseService {
  private readonly providers$ = new BehaviorSubject<Provider[]>([]);

  public readonly providers = toSignal(this.providers$);

  constructor(http: HttpClient, userService: UserService) {
    super()
    this.all().pipe(first()).subscribe((response) => {
      this.providers$.next(response.body);
    });
  }

  private all(): Observable<HttpResponse<Provider[]>> {
    return this.http.get<Provider[]>(`${this.apiURL}/providers`, this.getParams());
  }

  create(data: Provider): Observable<HttpResponse<Provider>> {
      return this.http
        .post<Provider>(`${this.apiURL}/providers`, { provider: data }, this.getOptions())
        .pipe(tap((response) => {
          this.providers$.next([...this.providers$.value, response.body]);
        }));
  }

  requestPricing(params: RequestPricingParams): Observable<HttpResponse<void>> {
    return this.http
      .post<void>(`${this.apiURL}/providers/request_pricing`, params, this.getOptions());
  }

  sendBuyOrder(params: SendBuyOrderParams): Observable<HttpResponse<void>> {
    return this.http
      .post<void>(`${this.apiURL}/providers/send_buy_order`, params, this.getOptions());
  }

  getPricing(access_token: string): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.apiURL}/providers/pricing`, this.getParams({ access_token }));
  }

  getBuyOrder(access_token: string): Observable<HttpResponse<any>> {
    return this.http
      .get<any>(`${this.apiURL}/providers/buy_order`, this.getParams({ access_token }));
  }

  find(id: number): Provider {
    return this.providers$.value.find((provider) => provider.id === id);
  }

  getFromIds(provider_ids: number[]): Provider[] {
    return this.providers$.value
      .filter((provider) => provider_ids.includes(provider.id));
  }

  get(id: number) {
    return of(this.providers$.value.find(provider => provider.id === id));
  }

  search({ query }: SearchParams) {
    return of({
      body: {
        items: this.providers$.value.filter((provider: Provider) => {
          return provider.name.includes(query)
            || provider.email?.includes(query)
            || provider.rut?.includes(query);
        
        }),
      },
    });
  }
}

interface SearchParams {
  query: string;
}

interface RequestPricingParams {
  provider_ids?: number[];
  work_instance_id?: number;
  inventory_action_for_pricing_id?: number;
}

interface SendBuyOrderParams {
  inventory_action_for_buy_order_id?: number;
}
